import { useEffect, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

const digits = (s) => (s || "").replace(/\D/g, "");

export default function QuickClientDialog({ open, onClose, onSelect }) {
  const [form, setForm] = useState({});
  const [dups, setDups] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setForm({}); setDups([]); } }, [open]);

  useEffect(() => {
    if (!open) return;
    const name = (form.name || "").trim(), ph = digits(form.phone);
    if (name.length < 2 && ph.length < 6) { setDups([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/clients");
        const n = name.toLowerCase();
        setDups(data.filter((c) => (n.length >= 2 && (c.name || "").trim().toLowerCase() === n) || (ph.length >= 6 && digits(c.phone).endsWith(ph.slice(-9)))));
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [form.name, form.phone, open]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    if (!(form.name || "").trim()) return toast.error("Inserisci il nome del cliente");
    setSaving(true);
    try { const { data } = await api.post("/clients", form); toast.success("Cliente creato e collegato"); onSelect(data); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="quick-client-dialog">
        <DialogHeader><DialogTitle className="font-head">Nuovo cliente</DialogTitle><DialogDescription>Verrà salvato anche nella sezione Clienti.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nome e cognome *</Label><Input autoFocus data-testid="qc-name" value={form.name || ""} onChange={set("name")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Telefono</Label><Input data-testid="qc-phone" type="tel" value={form.phone || ""} onChange={set("phone")} /></div>
            <div><Label>Email</Label><Input data-testid="qc-email" type="email" value={form.email || ""} onChange={set("email")} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Indirizzo</Label><Input data-testid="qc-address" value={form.address || ""} onChange={set("address")} /></div>
            <div><Label>Località</Label><Input data-testid="qc-locality" value={form.locality || ""} onChange={set("locality")} /></div>
          </div>
          <div><Label>Note</Label><Textarea data-testid="qc-notes" rows={2} value={form.notes || ""} onChange={set("notes")} /></div>
          {dups.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="qc-duplicates">
              <p className="text-sm font-semibold text-amber-800 flex items-center gap-1"><AlertTriangle size={15} /> Esiste già un cliente con questo nome o telefono</p>
              {dups.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate">{c.name} <span className="text-muted-foreground">· {c.phone || c.locality || "—"}</span></span>
                  <Button size="sm" variant="outline" data-testid={`qc-use-${c.id}`} onClick={() => { onSelect(c); onClose(); }}>Usa questo</Button>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter className="flex-row gap-2 justify-end">
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button className="bg-brand hover:bg-[#A8472F] text-white" data-testid="qc-save" disabled={saving} onClick={save}>{dups.length ? "Crea comunque" : "Salva cliente"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
